import { getApiUrl } from "@/utils/api";
import { useState, useEffect, useRef, Fragment } from "react";
import { createPortal } from "react-dom";
import { useParams, Link } from "react-router-dom";
import { 
  ArrowLeft, Bed, Bath, MapPin, X, Maximize, Home, Key, Train, 
  Car, Compass, Calendar, Mail, Phone,
  Wifi, Thermometer, TreePine, Waves, ShowerHead, Utensils, Sofa, 
  ParkingCircle, Shield, Zap, Wind, Sun, Moon, Mountain, Building,
  DoorOpen, Fence, Flower2, Warehouse, Star, Check, Download,
  ZoomIn, ZoomOut, ChevronLeft, ChevronRight, Settings, Pencil, MessageCircle, Images, ShieldCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Footer } from "@/components/layout/Footer";
import PageShell from "@/components/PageShell";
import { EnquiryForm } from "@/components/EnquiryForm";
import { VideoEmbed } from "@/components/VideoEmbed";
import PropertyGalleryModal from "@/components/PropertyGalleryModal";
import MediaGalleryGrid from "@/components/MediaGalleryGrid";
import VideoPosterFrame from "@/components/VideoPosterFrame";
import { GoogleMap } from "@/components/GoogleMap";
import { ImageCarousel } from "@/components/ImageCarousel";
import { PropertyImage } from "@/components/PropertyImage";
import ShareButton from "@/components/ShareButton";
import { getAgentWhatsAppHref } from "@/utils/whatsapp";
import { resolveImageUrl, getOptimizedImageUrl, getResponsiveSrcSet } from "@/utils/imageUrl";
import ProgressiveImg from "@/components/ProgressiveImg";
import { useSEO } from "@/hooks/useSEO";
import { useCmsFieldVisibility } from "@/hooks/useCmsFieldVisibility";
import { toast } from "sonner";
import axios from "axios";
import { usePageContent } from "@/hooks/usePageContent";
import { trackEvent } from "@/hooks/useAnalyticsTracker";
import { useBackHref } from "@/hooks/useBackHref";

const API = getApiUrl();

// Same canonical fallback used by CMS Fields admin + the capture form, so
// ordering here matches even before the group-order endpoint responds.
const DEFAULT_GROUP_ORDER = [
  "property", "address", "price", "contact", "agent", "assets",
  "features", "seo", "listing_status", "property_status", "other",
];

// Feature icon mapping
const featureIcons = {
  // Heating & Cooling
  'heating': Thermometer,
  'central heating': Thermometer,
  'gas heating': Thermometer,
  'underfloor heating': Thermometer,
  'air conditioning': Wind,
  'ac': Wind,
  'cooling': Wind,
  // Outdoor
  'garden': TreePine,
  'private garden': TreePine,
  'landscaped garden': TreePine,
  'balcony': Mountain,
  'terrace': Mountain,
  'patio': Flower2,
  'pool': Waves,
  'swimming pool': Waves,
  'hot tub': Waves,
  'jacuzzi': Waves,
  'fence': Fence,
  'fenced': Fence,
  // Parking
  'parking': ParkingCircle,
  'garage': Warehouse,
  'driveway': ParkingCircle,
  'car port': ParkingCircle,
  // Interior
  'kitchen': Utensils,
  'modern kitchen': Utensils,
  'fitted kitchen': Utensils,
  'living room': Sofa,
  'lounge': Sofa,
  'bathroom': ShowerHead,
  'en-suite': ShowerHead,
  'ensuite': ShowerHead,
  'shower': ShowerHead,
  // Technology
  'wifi': Wifi,
  'broadband': Wifi,
  'smart home': Zap,
  'ev charging': Zap,
  'solar': Sun,
  'solar panels': Sun,
  // Security
  'security': Shield,
  'alarm': Shield,
  'cctv': Shield,
  'gated': Shield,
  // Views & Location
  'views': Mountain,
  'sea view': Waves,
  'city view': Building,
  'countryside': TreePine,
  // General
  'double glazing': DoorOpen,
  'conservatory': Sun,
  'utility': Warehouse,
  'storage': Warehouse,
  'loft': Building,
  'cellar': Building,
  'fireplace': Thermometer,
  'wood burner': Thermometer,
  'period features': Star,
  'newly renovated': Star,
  'modern': Star,
  'spacious': Maximize,
  'open plan': DoorOpen,
};

// Get icon for a feature
const getFeatureIcon = (feature) => {
  const lowerFeature = feature.toLowerCase().trim();
  
  // Direct match
  if (featureIcons[lowerFeature]) {
    return featureIcons[lowerFeature];
  }
  
  // Partial match
  for (const [key, icon] of Object.entries(featureIcons)) {
    if (lowerFeature.includes(key) || key.includes(lowerFeature)) {
      return icon;
    }
  }
  
  // Default
  return Check;
};

const formatPrice = (price, currency = "£") => {
  if (!price && price !== 0) return "Price on Application";
  try {
    return `${currency}${Number(price).toLocaleString()}`;
  } catch {
    return `${currency}${price}`;
  }
};

// Format date as relative time (e.g., "2 months ago", "5 days ago")
const formatRelativeDate = (dateStr) => {
  if (!dateStr) return null;
  
  const date = new Date(dateStr);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return "Today";
  } else if (diffDays === 1) {
    return "Yesterday";
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return months === 1 ? "1 month ago" : `${months} months ago`;
  } else {
    const years = Math.floor(diffDays / 365);
    return years === 1 ? "1 year ago" : `${years} years ago`;
  }
};

/**
 * Inline markdown-lite bold: convert `**text**` → <strong>text</strong>.
 * Everything else is left as plain text so it still respects
 * `whitespace-pre-wrap` on the parent (preserves newlines and spaces).
 * Kept intentionally minimal — no italics, links or lists — because
 * property descriptions are user-entered plain text with only occasional
 * emphasis markers.
 */
function renderInlineBold(text) {
  if (text == null || text === "") return text;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

/**
 * A line starting with "* " (e.g. an agent typing a quick bullet list
 * into the plain-text description box) reads as a bullet — render it
 * as a tick + the rest of the line instead of a literal asterisk.
 * Splits on newlines so each line can be independently checked, and
 * re-joins with <br/> to keep `whitespace-pre-wrap` line spacing.
 */
function renderDescription(text) {
  if (text == null || text === "") return text;
  const lines = String(text).split("\n");
  return lines.map((line, i) => {
    const bulletMatch = line.match(/^(\s*)\*\s+(.*)$/s);
    return (
      <Fragment key={i}>
        {i > 0 && <br />}
        {bulletMatch ? (
          <span className="inline-flex items-start gap-2">
            <Check className="h-4 w-4 mt-1 shrink-0 text-primary" />
            <span>{renderInlineBold(bulletMatch[2])}</span>
          </span>
        ) : (
          renderInlineBold(line)
        )}
      </Fragment>
    );
  });
}

export default function PropertyPage({ propertyId: propPropertyId, isModal = false } = {}) {
  const params = useParams();
  const id = propPropertyId ?? params.id;
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const { content: pageContent, get: cms } = usePageContent("property");
  const vis = useCmsFieldVisibility();
  const [groupOrder, setGroupOrder] = useState(DEFAULT_GROUP_ORDER);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/cms-group-order`).then((r) => {
      if (!cancelled && Array.isArray(r.data?.order) && r.data.order.length) {
        setGroupOrder(r.data.order);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const descriptionRef = useRef(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [mediaGalleryOpen, setMediaGalleryOpen] = useState(false);
  // Currently featured (top) gallery item. Clicking a thumbnail below promotes
  // it into this slot; clicking the hero itself opens the lightbox.
  const [selectedGalleryIndex, setSelectedGalleryIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [floorplanOpen, setFloorplanOpen] = useState(false);
  const [floorplanZoom, setFloorplanZoom] = useState(1);
  const brochureRef = useRef(null);
  const back = useBackHref("/properties");

  // If the viewer is signed in as a property-admin agent AND owns this
  // property, we hide the Enquire CTAs and show an "Edit listing" shortcut.
  const [isOwnListing, setIsOwnListing] = useState(false);
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/property-admin/me`, { withCredentials: true });
        if (cancelled) return;
        const owns = (data?.linked_properties || []).some((p) => p.property_id === id);
        setIsOwnListing(owns);
      } catch {
        setIsOwnListing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    fetchProperty();
  }, [id]);

  // Track page view when property is loaded — include referrer and UTM params
  useEffect(() => {
    if (property && id) {
      const params = new URLSearchParams(window.location.search);
      const ref = document.referrer ? new URL(document.referrer).hostname : "";
      const qs = new URLSearchParams({
        ref,
        utm_campaign: params.get("utm_campaign") || "",
        utm_source: params.get("utm_source") || ""
      }).toString();
      axios.post(`${API}/properties/${id}/track-view?${qs}`).catch(() => {});
      // Remember this property as the checkout "context" — the Info tab in
      // the property-admin dashboard uses this to surface unassociated order
      // items to the right agent. Overwritten every time a property page is
      // viewed; cleared on successful checkout.
      try { sessionStorage.setItem("context_property_id", id); } catch { /* ignore */ }
      // Also track via new analytics system
      trackEvent("property_view", {
        property_id: id,
        page_url: window.location.pathname,
        page_title: document.title,
      });
    }
  }, [property, id]);

  // Track time on page
  useEffect(() => {
    if (!property || !id) return;
    const start = Date.now();
    const handleUnload = () => {
      const duration = Math.round((Date.now() - start) / 1000);
      if (duration > 2) {
        navigator.sendBeacon(
          `${API}/properties/${id}/track-time`,
          JSON.stringify({ duration })
        );
      }
    };  window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [property, id]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}`);
      setProperty(response.data);
    } catch (error) {
      console.error("Error fetching property:", error);
    } finally {
      setLoading(false);
    }
  };

  const trackPhoneCall = () => {
    if (id) {
      axios.post(`${API}/properties/${id}/track-call`).catch(() => {});
      // Also track via new analytics system
      trackEvent("call_click", {
        property_id: id,
        page_url: window.location.pathname,
        page_title: document.title,
      });
    }
  };

  const trackWhatsAppClick = () => {
    if (id) {
      axios.post(`${API}/properties/${id}/track-whatsapp`).catch(() => {});
      trackEvent("whatsapp_click", {
        property_id: id,
        page_url: window.location.pathname,
        page_title: document.title,
      });
    }
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setZoomLevel(1);
    setLightboxOpen(true);
  };

  // Keyboard shortcuts — left/right arrows navigate the hero (when the
  // lightbox is closed) or the lightbox (when it's open). Escape closes the
  // lightbox. Ignored while typing in inputs/textareas.
  useEffect(() => {
    if (!property) return;
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (lightboxOpen) handlePrevImage();
        else heroPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (lightboxOpen) handleNextImage();
        else heroNext();
      } else if (e.key === "Escape" && lightboxOpen) {
        setLightboxOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [property, lightboxOpen, lightboxIndex, selectedGalleryIndex]);

  // Hero-slot navigation — cycles the selected image on the top featured
  // slot without opening the lightbox. Guarded with a stopPropagation so
  // clicking the arrow doesn't bubble into the hero's onClick (which opens
  // the lightbox).
  const _galleryLength = () => {
    if (property?.gallery && property.gallery.length > 0) return property.gallery.length;
    return property?.main_image ? 1 : 0;
  };
  const heroPrev = (e) => {
    e?.stopPropagation?.();
    const n = _galleryLength();
    if (n > 0) setSelectedGalleryIndex((prev) => (prev - 1 + n) % n);
  };
  const heroNext = (e) => {
    e?.stopPropagation?.();
    const n = _galleryLength();
    if (n > 0) setSelectedGalleryIndex((prev) => (prev + 1) % n);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 0.5));
  };

  const handlePrevImage = () => {
    const total = gallery.length;
    if (total > 0) {
      const next = (lightboxIndex - 1 + total) % total;
      setLightboxIndex(next);
      setSelectedGalleryIndex(next);
      setZoomLevel(1);
    }
  };

  const handleNextImage = () => {
    const total = gallery.length;
    if (total > 0) {
      const next = (lightboxIndex + 1) % total;
      setLightboxIndex(next);
      setSelectedGalleryIndex(next);
      setZoomLevel(1);
    }
  };

  // Generate PDF brochure
  const generateBrochure = async () => {
    setPdfGenerating(true);
    try {
      toast.info("Generating PDF brochure...");
      
      // Use backend endpoint which downloads external images server-side (no CORS issues)
      const response = await axios.get(`${API}/property/${id}/generate-pdf`, {
        responseType: 'blob',
        timeout: 60000
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${property.title.replace(/[^a-z0-9]/gi, '_')}_brochure.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Track brochure download
      trackEvent("brochure_download", {
        property_id: id,
        page_url: window.location.pathname,
        page_title: document.title,
      });
      
      toast.success("Brochure downloaded successfully!");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate brochure. Please try again.");
    } finally {
      setPdfGenerating(false);
    }
  };

  // Get current page URL for sharing
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  // Pre-filled WhatsApp opener — kept short so it survives Android URL limits.
  // Falls back gracefully if any field is missing. Shared with the Share
  // modal's WhatsApp option (utils/whatsapp.js) so both behave identically.
  const whatsappHref = getAgentWhatsAppHref(property, currentUrl);
  // Modal-specific visibility: agent can hide their phone chip on the
  // quick-view modal (defaults on). Full page always shows them.
  // The WhatsApp chip is controlled solely by "Accept WhatsApp communication"
  // (agent_whatsapp_enabled) — no separate modal gate.
  const showPhone = isModal ? property?.agent_show_phone_in_modal !== false : true;
  const showWhatsAppChip = property?.agent_whatsapp_enabled !== false;

  // Check if header/footer/back link should be shown (default to true if not set)
  const showHeader = property?.show_header !== false;
  const showFooter = property?.show_footer !== false;
  const showBackLink = property?.show_back_link !== false;

  // Gallery items are now objects `{media_url, media_type, caption, sort_order}`.
  // Sort by sort_order and keep the object shape so we can key rendering off
  // `media_type` (5 = Video → <video>, 6 = Virtual Tour → <iframe>, everything
  // else → <img>). Legacy string entries are still tolerated as a safety net.
  const _normaliseItem = (item, idx) => {
    if (item && typeof item === "object") {
      return {
        url: item.media_url || "",
        media_type: Number(item.media_type) || 1,
        caption: item.caption || "",
        sort_order: Number.isFinite(item.sort_order) ? item.sort_order : idx,
      };
    }
    const url = typeof item === "string" ? item : "";
    const isVideoExt = /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url);
    return { url, media_type: isVideoExt ? 5 : 1, caption: "", sort_order: idx };
  };
  const galleryItems = (() => {
    if (property?.gallery && property.gallery.length > 0) {
      return property.gallery
        .map(_normaliseItem)
        .filter((x) => x.url)
        .sort((a, b) => a.sort_order - b.sort_order);
    }
    if (property?.main_image) return [_normaliseItem(property.main_image, 0)];
    return [];
  })();
  // Flat URL array kept for the lightbox/thumbnail loops that still expect strings.
  const gallery = galleryItems.map((it) => it.url);
  const safeSelectedIndex = Math.min(selectedGalleryIndex, Math.max(gallery.length - 1, 0));
  const featuredItem = galleryItems[safeSelectedIndex] || galleryItems[0] || null;
  const featuredImage = featuredItem?.url || null;
  const featuredMediaType = featuredItem?.media_type || 1;
  const featuredIsVideo = featuredMediaType === 5;
  const featuredIsVirtualTour = featuredMediaType === 6;
  const featuredCaption = featuredItem?.caption || "";

  // Generate SEO data - use property-specific SEO fields or fallback to property data
  const seoTitle = property?.seo_title || (property ? `${property.title} | ${formatPrice(property.price, property.currency)} | SellLocalHomes` : 'Loading... | SellLocalHomes');
  const seoDescription = property?.seo_description || (property ? `${property.title} - ${property.beds} bed, ${property.baths} bath property in ${property.display_address}. ${property.description?.substring(0, 120)}...` : 'Loading property details...');
  const seoKeywords = property?.seo_keywords || (property ? `${property.display_address}, ${property.beds} bedroom, property for sale, ${property.property_type || 'house'}` : 'property for sale');
  const seoRobots = property?.seo_robots || "index, follow";
  const canonicalUrl = property ? `https://selllocalhomes.co.uk/property/${property.seo_slug || property.property_id}` : '';

  // Apply SEO tags via direct DOM manipulation (more reliable than react-helmet-async with React 19)
  useSEO(
    {
      seo_title: seoTitle,
      seo_description: seoDescription,
      seo_keywords: seoKeywords,
      seo_robots: seoRobots,
      canonical_url: canonicalUrl
    },
    { 
      og_type: "website", 
      og_image: featuredImage ? resolveImageUrl(featuredImage) : undefined 
    }
  );

  // Preload every image in the gallery as soon as the property loads.
  //
  // The featured hero and thumbnails only render one image at a time. Without
  // this, clicking a thumbnail (or arrow-key nav) triggers a fresh network
  // fetch for that image — noticeably janky on slow mobile connections.
  //
  // Two-tier preload strategy:
  //  * Featured image (index 0) is fetched with `fetchpriority="high"` and
  //    fully `.decode()`'d — so opening the lightbox is instant.
  //  * Remaining images are queued at normal priority so they don't compete
  //    with the initial page render, but are ready by the time the user
  //    starts navigating the gallery.
  //
  // We use both `<link rel="preload">` (fetch scheduler hint) and
  // `new Image()` (in-memory cache + `.decode()`) because some browsers skip
  // `<link preload>` for cross-origin without matching `crossorigin` attrs.
  //
  // Skips videos (media_type === 5) and virtual tours (media_type === 6) —
  // those are heavy and not needed until the user actively opens them.
  useEffect(() => {
    if (!property) return;
    const items = Array.isArray(property.gallery) ? property.gallery : [];
    const urls = items
      .filter((it) => {
        const t = typeof it === "object" ? (it.media_type ?? 1) : 1;
        return t !== 5 && t !== 6;
      })
      .map((it) => (typeof it === "string" ? it : it?.media_url || it?.url))
      .filter(Boolean)
      .map((u) => resolveImageUrl(u));
    if (property.main_image) urls.unshift(resolveImageUrl(property.main_image));
    // De-dupe (the featured image is often already in the gallery).
    const uniqueUrls = Array.from(new Set(urls));

    const links = [];
    uniqueUrls.forEach((url, idx) => {
      const isFirst = idx === 0;
      // Warm the in-memory image cache + decode so `<img>` renders instant.
      const img = new Image();
      img.decoding = "async";
      if (isFirst && "fetchPriority" in img) img.fetchPriority = "high";
      img.src = url;
      if (img.decode) img.decode().catch(() => {});

      // Preload hint for the browser's fetch scheduler. First image is
      // marked high priority so it wins the network race with any late CSS
      // or analytics beacons the page fires.
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = url;
      if (isFirst) link.setAttribute("fetchpriority", "high");
      document.head.appendChild(link);
      links.push(link);
    });
    return () => { links.forEach((l) => l.remove()); };
  }, [property]);

  // Add JSON-LD structured data for real estate
  useEffect(() => {
    if (!property) return;
    const existingScript = document.querySelector('script[data-property-schema]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-property-schema', 'true');
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "RealEstateListing",
      "name": property.title,
      "description": property.description,
      "url": canonicalUrl,
      "image": resolveImageUrl(featuredImage),
      "price": property.price,
      "priceCurrency": "USD",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": property.display_address,
        "streetAddress": property.cms_House_Name_Number || ""
      },
      "numberOfRooms": property.beds,
      "numberOfBathroomsTotal": property.baths
    });
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('script[data-property-schema]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [property, canonicalUrl, featuredImage]);

  if (loading) {
    const loadingInner = (
      <div className="min-h-screen">
        <div className="pt-12 md:pt-16 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="animate-pulse space-y-8">
            <div className="h-8 w-48 bg-muted rounded" />
            <div className="aspect-video bg-muted rounded" />
          </div>
        </div>
      </div>
    );
    return isModal ? loadingInner : <PageShell>{loadingInner}</PageShell>;
  }

  if (!property) {
    const notFoundInner = (
      <div className="min-h-screen">
        <div className="pt-12 md:pt-16 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center py-20">
          <h1 className="font-serif text-3xl mb-4">{cms("not_found_title", "Property Not Found")}</h1>
          <Link to="/properties">
            <Button className="bg-primary text-white hover:bg-primary/90 rounded-none">
              {cms("not_found_btn", "Browse Properties")}
            </Button>
          </Link>
        </div>
      </div>
    );
    return isModal ? notFoundInner : <PageShell>{notFoundInner}</PageShell>;
  }

  const propertyBody = (
    <div className="min-h-screen" data-testid="property-page" ref={brochureRef}>
      {/* Navigation removed - add via CMS if needed */}

      <div className={isModal ? "pt-2 pb-6" : (showHeader ? "pt-28 pb-20" : "py-12")}>
        <div className={isModal ? "max-w-7xl mx-auto px-3 md:px-6" : "max-w-7xl mx-auto px-6 md:px-12 lg:px-24"}>
          {/* Back Link — hidden in modal mode since closing the modal
              already returns to the listing behind it. */}
          {showBackLink && !isModal && (
            <Link
              to={back.href}
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 group"
              data-testid="back-to-properties"
            >
              <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm">{cms("back_link", back.label)}</span>
            </Link>
          )}

          {/* Property Title & Address - moved above the featured image */}
          <div className="mb-4 animate-fade-in-up" data-testid="property-title-block">
            <h1 className="font-serif text-xl sm:text-2xl md:text-3xl font-medium text-foreground" data-testid="property-title">
              {property.title}
            </h1>
            <div className="flex items-center gap-2 text-muted-foreground mt-2">
              <MapPin size={18} />
              <span className="text-base sm:text-lg" data-testid="property-location">{property.display_address}</span>
            </div>
          </div>

          {/* 1. FEATURED IMAGE - Hero Style with Logo Overlays (uses currently selected gallery item) */}
          <div className="mb-4 animate-fade-in-up" data-testid="featured-image-section">
            <div className="relative aspect-[21/9] overflow-hidden cursor-pointer image-hover bg-black" onClick={() => openLightbox(safeSelectedIndex)}>
              {featuredImage ? (
                featuredIsVideo ? (
                  <video
                    key={featuredImage}
                    src={resolveImageUrl(featuredImage)}
                    className="w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    data-testid="featured-video"
                  />
                ) : featuredIsVirtualTour ? (
                  <iframe
                    key={featuredImage}
                    src={featuredImage}
                    title={featuredCaption || "Virtual tour"}
                    className="w-full h-full border-0"
                    allow="fullscreen; xr-spatial-tracking"
                    allowFullScreen
                    data-testid="featured-virtual-tour"
                  />
                ) : (
                  <ProgressiveImg
                    src={featuredImage}
                    srcSet={getResponsiveSrcSet(featuredImage)}
                    sizes="100vw"
                    alt={property.title}
                    loading="eager"
                    className="w-full h-full"
                  />
                )
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <span className="text-muted-foreground">No image available</span>
                </div>
              )}
              {/* Agent logo + Share — top right */}
              <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
                {property.agent_logo && (
                  <div className="bg-white/90 backdrop-blur-sm p-0.5 rounded">
                    <img
                      src={resolveImageUrl(property.agent_logo)}
                      alt={property.agent_name || 'Agent'}
                      className="h-5 w-auto max-w-[56px] object-contain slh-no-img-style"
                    />
                  </div>
                )}
                <ShareButton property={property} url={currentUrl} content={pageContent} size="md" />
              </div>

              {/* Transaction status badge - top left */}
              {property.transaction_status && (() => {
                const map = {
                  "SSTC":        "bg-amber-500",
                  "SSTCM":       "bg-amber-600",
                  "Under Offer": "bg-purple-600",
                  "Reserved":    "bg-blue-600",
                  "Let Agreed":  "bg-teal-600",
                };
                const bg = map[property.transaction_status];
                if (!bg) return null;
                return (
                  <div
                    className={`absolute top-4 left-4 z-20 ${bg} text-white uppercase tracking-wider font-semibold px-4 py-1.5 text-sm shadow-lg`}
                    data-testid={`hero-status-badge-${property.transaction_status.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {property.transaction_status}
                  </div>
                );
              })()}

              {/* Hero prev / next navigation — only visible when there are 2+ items */}
              {gallery.length > 1 && (
                <>
                  <button
                    onClick={heroPrev}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-white bg-black/40 hover:bg-black/70 rounded-full transition-colors z-20 backdrop-blur-sm"
                    title="Previous image"
                    data-testid="hero-prev-btn"
                    aria-label="Previous image"
                  >
                    <ChevronLeft size={26} />
                  </button>
                  <button
                    onClick={heroNext}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-white bg-black/40 hover:bg-black/70 rounded-full transition-colors z-20 backdrop-blur-sm"
                    title="Next image"
                    data-testid="hero-next-btn"
                    aria-label="Next image"
                  >
                    <ChevronRight size={26} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setMediaGalleryOpen(true); }}
                    className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-white/90 text-[#0b1638] text-xs font-semibold rounded-md shadow-lg z-20 transition-colors"
                    data-testid="view-all-photos-btn"
                  >
                    <Images className="h-3.5 w-3.5" />
                    View all {gallery.length} photos
                  </button>
                </>
              )}
              {/* Bottom-left stack: caption (if any) + Request Viewing CTA */}
              <div className="absolute bottom-3 left-3 z-20 flex flex-col items-start gap-2 max-w-[70%]">
                {featuredCaption && (
                  <div
                    className="max-w-full px-3 py-1.5 bg-black/55 text-white text-sm rounded backdrop-blur-sm truncate"
                    data-testid="hero-caption"
                  >
                    {featuredCaption}
                  </div>
                )}
                {!isOwnListing && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      trackEvent("enquire_click", { property_id: id, source: "hero_image" });
                      setShowEnquiry(true);
                    }}
                    className="flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/90 text-white text-sm font-medium rounded-md shadow-lg transition-colors"
                    data-testid="request-viewing-btn-hero"
                  >
                    <Mail size={14} />
                    Request Viewing
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Added + Price on one line, Featured by + Social share below */}
          <div className="mb-4 animate-fade-in-up space-y-3">
            {/* Row 1: Added date (left) and Price (right) */}
            <div className="flex flex-wrap items-start justify-between gap-4">
              {property.date_added ? (
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Calendar size={16} />
                  <span>{cms("added_label", "Added")} {formatRelativeDate(property.date_added)}</span>
                </div>
              ) : <div />}
              <div className="ml-auto text-right">
                {property.price_label && property.price_label !== "Default" && property.price_label !== "Price" ? (
                  <div
                    className="text-xs uppercase tracking-wider font-medium text-muted-foreground"
                    data-testid="property-price-label"
                  >
                    {property.price_label}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">Price</div>
                )}
                <div className="font-serif text-2xl md:text-3xl font-medium text-primary leading-tight" data-testid="property-price">
                  {formatPrice(property.price, property.currency)}
                </div>
              </div>
            </div>
            {/* Row 2: Featured by */}
            {(property.agent_logo || property.agent_name || property.agent_tel) && (
              <div className="flex items-center gap-3 text-sm" data-testid="featured-by-agent">
                <span className="text-muted-foreground">{cms("featured_by", "Featured by")}</span>
                {property.agent_logo && (
                  <img 
                    src={resolveImageUrl(property.agent_logo)} 
                    alt={property.agent_name || 'Agent'} 
                    className="h-8 w-auto max-w-[100px] object-contain slh-no-img-style"
                    title={property.agent_name || 'Agent'}
                  />
                )}
                {property.agent_name && (
                  <span className="font-medium text-primary" data-testid="featured-by-agent-name">
                    {property.agent_name}
                  </span>
                )}
                {property.agent_id_verified && (
                  <span
                    className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-500 text-white"
                    title="This agent has passed ID verification"
                    data-testid="featured-by-agent-verified-badge"
                  >
                    <ShieldCheck size={12} /> Verified
                  </span>
                )}
                {property.agent_tel && showPhone && (
                  <a
                    href={`tel:${property.agent_tel}`}
                    onClick={trackPhoneCall}
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                    data-testid="featured-by-agent-tel"
                  >
                    <Phone size={14} /> {property.agent_tel}
                  </a>
                )}
                {whatsappHref && showWhatsAppChip && (
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={trackWhatsAppClick}
                    className="inline-flex items-center gap-1 text-[#25D366] hover:underline"
                    title="Chat on WhatsApp"
                    data-testid="featured-by-agent-whatsapp"
                  >
                    <MessageCircle size={14} /> WhatsApp
                  </a>
                )}
              </div>
            )}
          </div>

          {/* KEY INFO SECTION - reserved for future chip rows */}
          <div className="mb-8 animate-fade-in-up">
            {/* Property Details — chip row removed per user request */}
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-10">
              {/* Description, Features, Video, Gallery and Floorplan render
                  in CMS Fields group + display_order order (same ordering
                  used by the agent edit form and the public capture form). */}
              {(() => {
                const blocks = {
                  description: (
                    <div ref={descriptionRef} className="space-y-3 animate-fade-in-up animation-delay-100" data-testid="description-section">
                      <h2 className="font-serif text-xl font-medium">{cms("about_title", "About This Property")}</h2>
                      <p className="text-muted-foreground leading-relaxed text-base whitespace-pre-wrap" data-testid="property-description">
                        {renderDescription(
                          property.description && property.description.length > 200 && !isDescExpanded
                            ? property.description.substring(0, 200).trimEnd() + '…'
                            : property.description
                        )}
                      </p>
                      {property.description && property.description.length > 200 && (
                        <button
                          onClick={() => {
                            const nextExpanded = !isDescExpanded;
                            setIsDescExpanded(nextExpanded);
                            if (nextExpanded) {
                              setTimeout(() => {
                                descriptionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                              }, 50);
                            }
                          }}
                          className="text-sm font-medium text-primary underline underline-offset-2 hover:text-primary/80 transition-colors"
                          data-testid="read-more-description-btn"
                        >
                          {isDescExpanded ? "Show less" : "Read full description"}
                        </button>
                      )}
                    </div>
                  ),
                  features: property.features ? (
                    <div className="space-y-4 animate-fade-in-up">
                      <h2 className="font-serif text-xl font-medium">{cms("features_title", "Features")}</h2>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {property.features.split(',').map((feature, index) => {
                          const IconComponent = getFeatureIcon(feature);
                          return (
                            <div key={index} className="flex items-center gap-3 p-3 bg-muted/30 border border-border hover:bg-muted/50 transition-colors">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                <IconComponent size={16} className="text-primary" />
                              </div>
                              <span className="text-sm text-foreground">{feature.trim()}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null,
                  video_url: (property.video_url && property.show_video_thumbnail !== false) ? (
                    <div className="animate-fade-in-up animation-delay-200" data-testid="video-section">
                      <VideoEmbed
                        url={property.video_url}
                        videoType={property.video_type || "youtube"}
                        title={property.title}
                      />
                    </div>
                  ) : null,
                  gallery: (
                    <div className="animate-fade-in-up animation-delay-300" data-testid="gallery-section">
                      <h2 className="font-serif text-xl font-medium mb-4">Image Gallery</h2>
                      <p className="text-sm text-muted-foreground mb-4">Click any image to open the gallery</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {galleryItems.slice(0, 6).map((entry, index) => {
                          const img = entry.url;
                          const isVideo = entry.media_type === 5;
                          const isVirtualTour = entry.media_type === 6;
                          const isFloorplan = entry.media_type === 2;
                          const isEpc = entry.media_type === 3;
                          const isBrochure = entry.media_type === 4;
                          const isActive = index === safeSelectedIndex;
                          const typeLabel = isVideo ? "VIDEO"
                            : isVirtualTour ? "VIRTUAL TOUR"
                            : isFloorplan ? "FLOORPLAN"
                            : isEpc ? "EPC"
                            : isBrochure ? "BROCHURE"
                            : null;
                          return (
                          <div
                            key={index}
                            className={`aspect-[4/3] overflow-hidden cursor-pointer group relative bg-black transition-all ${isActive ? "ring-2 ring-primary ring-offset-2" : ""}`}
                            onClick={() => {
                              setSelectedGalleryIndex(index);
                              openLightbox(index);
                            }}
                            data-testid={`gallery-thumb-${index}`}
                          >
                            {isVideo ? (
                              <VideoPosterFrame src={resolveImageUrl(img)} />
                            ) : isVirtualTour ? (
                              <div className="w-full h-full bg-gradient-to-br from-primary/80 to-primary flex flex-col items-center justify-center text-white">
                                <Maximize size={32} className="mb-2 opacity-90" />
                                <span className="text-xs font-medium tracking-wide">3D TOUR</span>
                              </div>
                            ) : (
                              <ProgressiveImg
                                src={img}
                                sizes="(max-width: 768px) 50vw, 33vw"
                                alt={entry.caption || `${property.title} - Image ${index + 1}`}
                                loading="lazy"
                                className="w-full h-full transition-transform duration-300 group-hover:scale-105"
                              />
                            )}
                            {typeLabel && (
                              <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/70 text-white text-[10px] font-medium rounded z-10 pointer-events-none">
                                {typeLabel}
                              </div>
                            )}
                            {property.agent_logo && (
                              <div className="absolute top-1 right-1 bg-white/90 backdrop-blur-sm p-0.5 rounded z-10">
                                <img
                                  src={resolveImageUrl(property.agent_logo)}
                                  alt={property.agent_name || 'Agent'}
                                  className="h-3 w-auto max-w-[28px] object-contain slh-no-img-style"
                                />
                              </div>
                            )}
                            {!isVideo && !isVirtualTour && (
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                <div className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-xs font-medium px-2 py-1 bg-black/60 rounded">
                                  Click to open gallery
                                </div>
                              </div>
                            )}
                          </div>
                        );
                        })}
                      </div>
                      {gallery.length > 6 && (
                        <button
                          onClick={() => setMediaGalleryOpen(true)}
                          className="mt-3 text-sm text-primary hover:underline"
                          data-testid="view-all-images-btn"
                        >
                          View all {gallery.length} images
                        </button>
                      )}
                    </div>
                  ),
                  floorplan_image: property.floorplan_image ? (
                    <div className="space-y-4 animate-fade-in-up" data-testid="floorplan-section">
                      <div className="flex items-center justify-between">
                        <h2 className="font-serif text-xl font-medium">{cms("floorplan_title", "Floorplan")}</h2>
                        {property.floorplan_size && (
                          <span className="text-sm text-muted-foreground bg-muted px-3 py-1 rounded">
                            {property.floorplan_size}
                          </span>
                        )}
                      </div>
                      <div className="border border-border bg-muted/30 p-4">
                        <img
                          src={resolveImageUrl(property.floorplan_image)}
                          alt={`${property.title} - Floorplan`}
                          className="w-full h-auto object-contain max-h-96 cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => {
                            setFloorplanZoom(1);
                            setFloorplanOpen(true);
                          }}
                        />
                        <p className="text-xs text-muted-foreground text-center mt-2">Click to view full size with zoom</p>
                      </div>
                    </div>
                  ) : null,
                };
                const rank = (k) => {
                  const section = vis.settings[k]?.section || "property";
                  const idx = groupOrder.indexOf(section);
                  return idx === -1 ? groupOrder.length : idx;
                };
                const keys = Object.keys(blocks).sort((a, b) => {
                  const rA = rank(a);
                  const rB = rank(b);
                  if (rA !== rB) return rA - rB;
                  const oA = Number(vis.settings[a]?.display_order) || 0;
                  const oB = Number(vis.settings[b]?.display_order) || 0;
                  return oA - oB;
                });
                return keys.map((k) => <Fragment key={k}>{blocks[k]}</Fragment>);
              })()}

              {/* 4. GOOGLE MAP */}
              <div className="animate-fade-in-up animation-delay-400" data-testid="map-section">
                <GoogleMap 
                  address={property.cms_House_Name_Number}
                  location={property.display_address}
                  latitude={property.latitude}
                  longitude={property.longitude}
                  title={property.title}
                />
              </div>

              {/* Download Brochure */}
              <div className="animate-fade-in-up border-t border-border pt-8 mt-8">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h3 className="font-serif text-base font-medium">{cms("brochure_title", "Property Brochure")}</h3>
                    <p className="text-sm text-muted-foreground">{cms("brochure_desc", "Download a PDF version of this property listing")}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <ShareButton property={property} url={currentUrl} content={pageContent} variant="button" />
                    <Button
                      onClick={generateBrochure}
                      disabled={pdfGenerating}
                      className="bg-primary text-white hover:bg-primary/90 rounded-none flex items-center gap-2"
                      data-testid="download-brochure-btn"
                    >
                      {pdfGenerating ? (
                        <>
                          <span className="animate-spin">⏳</span>
                          {cms("generating_text", "Generating...")}
                        </>
                      ) : (
                        <>
                          <Download size={18} />
                          {cms("download_btn", "Download Brochure")}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar - Desktop only */}
            <div className="hidden lg:block lg:col-span-1">
              <div className="glass-card p-6 sticky top-28 space-y-4 animate-fade-in-up animation-delay-100">
                <div className="text-center space-y-1">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">
                    {property.price_label && property.price_label !== "Default" && property.price_label !== "Price"
                      ? property.price_label
                      : cms("sidebar_price_label", "Asking Price")}
                  </span>
                  <div className="font-serif text-xl font-medium text-primary">
                    {formatPrice(property.price, property.currency)}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    trackEvent("enquire_click", { property_id: id, source: "sidebar" });
                    setShowEnquiry(true);
                  }}
                  className={`block w-full ${isOwnListing ? "hidden" : ""}`}
                  data-testid="enquire-btn-sidebar"
                >
                  <Button className="w-full h-10 bg-primary text-white hover:bg-primary/90 rounded-none text-sm font-medium">
                    Request Viewing
                  </Button>
                </button>
                {isOwnListing && (
                  <Link
                    to={`/property-admin/dashboard?property_id=${encodeURIComponent(property.property_id)}`}
                    className="block"
                    data-testid="edit-listing-btn-sidebar"
                  >
                    <Button className="w-full h-10 bg-primary text-white hover:bg-primary/90 rounded-none text-sm font-medium">
                      <Pencil className="h-4 w-4 mr-1" /> Edit listing
                    </Button>
                  </Link>
                )}

                {property.agent_tel && showPhone && (
                  <p className="text-xs text-muted-foreground text-center" data-testid="sidebar-call-cta">
                    or call <a
                      href={`tel:${property.agent_tel}`}
                      className="text-primary font-medium"
                      onClick={trackPhoneCall}
                    >
                      {property.agent_tel}
                    </a>
                  </p>
                )}

                {/* Agent Info in Sidebar */}
                {(property.agent_logo || property.agent_name || property.agent_tel) && (
                  <div className="pt-4 border-t border-border" data-testid="sidebar-agent-block">
                    <p className="text-xs text-muted-foreground mb-2">Featured by</p>
                    <div className="flex items-center gap-3">
                      {property.agent_logo && (
                        <img 
                          src={resolveImageUrl(property.agent_logo)} 
                          alt={property.agent_name || 'Agent'} 
                          className="h-10 w-auto max-w-[80px] object-contain slh-no-img-style"
                        />
                      )}
                      <div className="min-w-0">
                        {property.agent_name && (
                          <span className="text-sm font-medium block truncate" data-testid="sidebar-agent-name">
                            {property.agent_name}
                          </span>
                        )}
                        {property.agent_tel && showPhone && (
                          <a
                            href={`tel:${property.agent_tel}`}
                            onClick={trackPhoneCall}
                            className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-0.5"
                            data-testid="sidebar-agent-tel"
                          >
                            <Phone size={12} /> {property.agent_tel}
                          </a>
                        )}
                        {whatsappHref && showWhatsAppChip && (
                          <a
                            href={whatsappHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={trackWhatsAppClick}
                            className="text-xs text-[#25D366] hover:underline inline-flex items-center gap-1 mt-0.5"
                            title="Chat on WhatsApp"
                            data-testid="sidebar-agent-whatsapp"
                          >
                            <MessageCircle size={12} /> Chat on WhatsApp
                          </a>
                        )}
                        {property.agent_website && (
                          <a 
                            href={property.agent_website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline block mt-0.5"
                          >
                            Visit Website
                          </a>
                        )}
                      </div>
                    </div>
                    {/* Manage Property Link */}
                    <Link 
                      to={`/property-admin/login?property=${property.property_id}${property.agent_id ? `&agent=${property.agent_id}` : ''}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors"
                      data-testid="manage-property-link"
                    >
                      <Settings size={14} />
                      {cms("manage_property_link", "Manage this property")}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Enquire CTA — portaled to <body> so it escapes any
          transformed ancestor (e.g. `animate-fade-in-up`) that would
          otherwise re-anchor `position: fixed` and let it disappear on
          scroll. When rendered inside the property details modal, the z-index
          sits above the modal overlay (z-[100]) so the button remains sticky
          and visible while the modal content scrolls. */}
      {createPortal(
        <div className="fixed bottom-6 right-6 z-[150] animate-fade-in-up">
          {isOwnListing ? (
            <Link
              to={`/property-admin/dashboard?property_id=${encodeURIComponent(property.property_id)}`}
              data-testid="edit-listing-btn-floating"
            >
              <Button className="h-11 px-5 bg-primary text-white hover:bg-primary/90 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2">
                <Pencil size={16} />
                <span className="text-sm font-medium">Edit listing</span>
              </Button>
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => {
                trackEvent("enquire_click", { property_id: id, source: "floating" });
                setShowEnquiry(true);
              }}
              data-testid="enquire-btn-floating"
            >
              <Button className="h-11 px-5 bg-primary text-white hover:bg-primary/90 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2">
                <Mail size={16} />
                <span className="text-sm font-medium">Request Viewing</span>
              </Button>
            </button>
          )}
        </div>,
        document.body
      )}

      {/* Enquiry Dialog — z-[200] so it always sits on top, even when
          "Request Viewing" is triggered from inside the quick-view property
          modal (z-[100]) or the full-screen media gallery (z-[180]). */}
      <Dialog open={showEnquiry} onOpenChange={setShowEnquiry}>
        <DialogContent
          className="sm:max-w-lg rounded-none z-[200]"
          overlayClassName="z-[200]"
          data-testid="enquiry-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              Enquire About {property.title}
            </DialogTitle>
          </DialogHeader>
          <EnquiryForm
            propertyId={property.property_id}
            propertyTitle={property.title}
            propertyPrice={property.formatted_price || (property.price ? `${property.currency || "£"}${Number(property.price).toLocaleString()}` : "")}
            propertyAddress={property.display_address}
            agentId={property.agent_id}
            agentName={property.agent_name}
            agentLogo={property.agent_logo}
            onSuccess={() => {
              setShowEnquiry(false);
              trackEvent("enquiry_submit", {
                property_id: id,
                page_url: window.location.pathname,
                page_title: document.title,
              });
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Reusable gallery modal — shared with property cards. Handles prev/next,
          keyboard arrows, thumbnail strip, and virtual-tour / video / photo. */}
      <PropertyGalleryModal
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        property={property}
        initialIndex={lightboxIndex}
      />

      {/* "Media gallery" full-page grid — shows every photo at once; tapping
          one opens the lightbox above at that index. */}
      <MediaGalleryGrid
        open={mediaGalleryOpen}
        onClose={() => setMediaGalleryOpen(false)}
        items={galleryItems}
        onSelectImage={openLightbox}
        suspendEscape={lightboxOpen}
      />

      {/* Floorplan Modal with Zoom */}
      {property?.floorplan_image && (
        <Dialog open={floorplanOpen} onOpenChange={setFloorplanOpen}>
          <DialogContent
            className="max-w-5xl h-[85vh] p-0 bg-white border-none rounded-none flex flex-col z-[210]"
            overlayClassName="z-[200]"
            hideClose
          >
            {/* Floorplan Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h3 className="font-serif text-lg font-medium">Floorplan</h3>
                {property.floorplan_size && (
                  <span className="text-sm text-muted-foreground">{property.floorplan_size}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setFloorplanZoom(prev => Math.max(prev - 0.25, 0.5))}
                  className="w-9 h-9 flex items-center justify-center text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 rounded transition-colors"
                  title="Zoom out"
                  disabled={floorplanZoom <= 0.5}
                >
                  <ZoomOut size={18} />
                </button>
                <span className="text-sm text-muted-foreground min-w-[50px] text-center">{Math.round(floorplanZoom * 100)}%</span>
                <button
                  onClick={() => setFloorplanZoom(prev => Math.min(prev + 0.25, 3))}
                  className="w-9 h-9 flex items-center justify-center text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 rounded transition-colors"
                  title="Zoom in"
                  disabled={floorplanZoom >= 3}
                >
                  <ZoomIn size={18} />
                </button>
                <button
                  onClick={() => setFloorplanOpen(false)}
                  className="w-9 h-9 flex items-center justify-center text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 rounded transition-colors ml-2"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Floorplan Image */}
            <div className="flex-1 overflow-auto flex items-center justify-center p-4 bg-gray-50">
              <div 
                style={{ 
                  transform: `scale(${floorplanZoom})`,
                  transformOrigin: 'center center',
                  transition: 'transform 0.2s ease'
                }}
              >
                <img
                  src={resolveImageUrl(property.floorplan_image)}
                  alt={`${property.title} - Floorplan`}
                  className="max-w-full max-h-[70vh] object-contain"
                  draggable={false}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showFooter && <Footer variant="property" propertyId={property.property_id} agentId={property.agent_id} pageName={property.title} />}
    </div>
  );

  return isModal ? propertyBody : <PageShell>{propertyBody}</PageShell>;
}
